import React from 'react';
import { usePromptContext } from '../src/context/PromptContext';
import Modal from './Modal';
import MovePromptModal from './MovePromptModal';
import { findPromptAndParentFolder, findFolder, getFolderContentMetrics } from '../utils/fileSystemUtils';

const AppModals: React.FC<any> = ({ actions }) => {
  const { 
    fileSystem, 
    setFileSystem, 
    setTemplates,
    pushHistory,
    clearSelection, 
    showToast, 
    modalState: modal, 
    setModalState 
  } = usePromptContext();

  const [folderNameError, setFolderNameError] = React.useState<string | null>(null);

  // Reset error when modal closes or changes
  React.useEffect(() => {
    setFolderNameError(null);
  }, [modal?.type]);

  if (!modal) return null;

  const validateFolderName = (name: string): boolean => {
    const trimmed = name.trim();
    if (!trimmed) {
      setFolderNameError('Der Ordnername darf nicht leer sein.');
      return false;
    }
    if (trimmed.length < 2) {
      setFolderNameError('Der Ordnername muss mindestens 2 Zeichen lang sein.');
      return false;
    }
    if (trimmed.length > 50) {
      setFolderNameError('Der Ordnername ist zu lang (max. 50 Zeichen).');
      return false;
    }
    // Optional: check for duplicates in the same level
    return true;
  };

  if (modal.type === 'create-folder') {
    return (
      <Modal title="Neuen Ordner erstellen" onClose={() => setModalState(null)}>
        <form
          onSubmit={(e: any) => {
            e.preventDefault();
            const name = e.currentTarget.folderName.value;
            if (validateFolderName(name)) {
              actions.handleCreateFolder(name.trim(), modal.parentId);
              setModalState(null);
            }
          }}
        >
          <label htmlFor="folderName" className="text-sm text-gray-600 dark:text-gray-300 font-medium">
            Ordnername
          </label>
          <input
            type="text"
            name="folderName"
            id="folderName"
            autoFocus
            required
            onChange={() => setFolderNameError(null)}
            className={`mt-1.5 w-full px-3 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-800/60 border transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/50 ${
              folderNameError ? 'border-red-500 ring-1 ring-red-500' : 'border-transparent'
            }`}
            placeholder="z.B. Marketing-Kampagne"
          />
          {folderNameError && <p className="mt-1.5 text-xs text-red-500 font-medium animate-shake">{folderNameError}</p>}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => setModalState(null)}
              className="btn btn-secondary btn-sm"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
            >
              Erstellen
            </button>
          </div>
        </form>
      </Modal>
    );
  }

  if (modal.type === 'rename-folder') {
    return (
      <Modal title="Ordner umbenennen" onClose={() => setModalState(null)}>
        <form
          onSubmit={(e: any) => {
            e.preventDefault();
            const name = e.currentTarget.folderName.value;
            if (validateFolderName(name)) {
              actions.handleRenameFolder(modal.folderId, name.trim());
              setModalState(null);
            }
          }}
        >
          <label htmlFor="folderName" className="text-sm text-gray-600 dark:text-gray-300 font-medium">
            Neuer Ordnername
          </label>
          <input
            type="text"
            name="folderName"
            id="folderName"
            autoFocus
            required
            defaultValue={modal.currentName}
            onChange={() => setFolderNameError(null)}
            className={`mt-1.5 w-full px-3 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-800/60 border transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/50 ${
              folderNameError ? 'border-red-500 ring-1 ring-red-500' : 'border-transparent'
            }`}
          />
          {folderNameError && <p className="mt-1.5 text-xs text-red-500 font-medium animate-shake">{folderNameError}</p>}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => setModalState(null)}
              className="btn btn-secondary btn-sm"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
            >
              Speichern
            </button>
          </div>
        </form>
      </Modal>
    );
  }

  if (modal.type === 'delete-folder') {
    const folder = findFolder(fileSystem, modal.folderId);
    const metrics = folder ? getFolderContentMetrics(folder) : { promptCount: 0, folderCount: 0 };
    
    return (
      <Modal title="Ordner löschen" onClose={() => setModalState(null)}>
        <p className="mb-4 text-gray-700 dark:text-gray-300 leading-relaxed">
          Sind Sie sicher, dass Sie den Ordner <span className="font-bold text-gray-900 dark:text-white">"{modal.folderName}"</span> löschen möchten?
        </p>
        
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-xl p-4 mb-4">
          <p className="text-xs font-bold text-red-700 dark:text-red-400 uppercase tracking-widest mb-3">
            Zusammenfassung der Inhalte:
          </p>
          <ul className="text-sm text-red-600 dark:text-red-400 space-y-2 font-medium">
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              <strong>{metrics.promptCount}</strong> Prompt(s)
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              <strong>{metrics.folderCount}</strong> Unterordner
            </li>
          </ul>
        </div>
        
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-6 italic">
          Der Ordner wird gelöscht, die enthaltenen Prompts werden in den Papierkorb verschoben.
        </p>
        
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setModalState(null)}
            className="btn btn-secondary btn-sm"
          >
            Abbrechen
          </button>
          <button
            onClick={() => actions.handleDeleteFolder(modal.folderId)}
            className="btn btn-danger btn-sm"
          >
            Ordner löschen
          </button>
        </div>
      </Modal>
    );
  }

  if (modal.type === 'delete-prompt') {
    return (
      <Modal title="Prompt löschen" onClose={() => setModalState(null)}>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
          Soll der Prompt <span className="font-bold text-gray-900 dark:text-white">"{modal.promptName}"</span> in den Papierkorb verschoben werden?
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setModalState(null)}
            className="btn btn-secondary btn-sm"
          >
            Abbrechen
          </button>
          <button
            onClick={() => actions.handleDeletePrompt(modal.promptId)}
            className="btn btn-danger btn-sm"
          >
            In den Papierkorb
          </button>
        </div>
      </Modal>
    );
  }

  if (modal.type === 'delete-forever') {
    return (
      <Modal title="Prompt endgültig löschen" onClose={() => setModalState(null)}>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
          Sind Sie sicher, dass Sie den Prompt <span className="font-bold text-gray-900 dark:text-white">"{modal.promptName}"</span> endgültig löschen möchten?
        </p>
        <p className="text-xs text-red-500 font-medium mb-6">
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setModalState(null)}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={() => actions.handlePermanentDeletePrompt(modal.promptId)}
            className="px-5 py-2 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 shadow-md shadow-red-500/20 transition-all active:scale-95"
          >
            Endgültig löschen
          </button>
        </div>
      </Modal>
    );
  }

  if (modal.type === 'move-prompt') {
    return (
      <MovePromptModal
        fileSystem={fileSystem}
        promptId={modal.promptId}
        currentFolderId={findPromptAndParentFolder(fileSystem, modal.promptId)?.parent.id || null}
        onMove={(promptId: string, targetFolderId: string) => {
          actions.handleMovePrompt(promptId, targetFolderId);
          setModalState(null);
        }}
        onClose={() => setModalState(null)}
      />
    );
  }

  if (modal.type === 'save-as-template') {
    return (
        <Modal title="Als Vorlage speichern" onClose={() => setModalState(null)}>
            <p className="text-gray-700 dark:text-gray-300">Möchten Sie diesen Prompt als Vorlage speichern?</p>
            <p className="text-sm text-gray-500 mt-2">Vorlagen können im Vorlagen-Manager verwaltet und für neue Prompts wiederverwendet werden.</p>
            <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setModalState(null)} className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">Abbrechen</button>
                <button 
                    onClick={() => {
                        const prompt = actions.allPrompts.find((p: any) => p.id === modal.promptId);
                        if (prompt) actions.handleSaveAsTemplate(prompt);
                        setModalState(null);
                    }}
                    className="px-4 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm"
                >
                    Speichern
                </button>
            </div>
        </Modal>
    );
  }

  if (modal.type === 'confirm-import') {
    return (
      <Modal title="Import bestätigen" onClose={() => setModalState(null)}>
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-xl p-4 mb-6">
          <p className="text-sm font-bold text-red-700 dark:text-red-400 mb-2">Achtung!</p>
          <p className="text-sm text-red-600 dark:text-red-400 leading-relaxed">
            Dadurch werden <span className="font-bold underline">alle</span> Ihre aktuellen Prompts und Ordner überschrieben.
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setModalState(null)}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={() => {
              pushHistory();
              setFileSystem(modal.data);
              if (modal.templates && Array.isArray(modal.templates)) {
                setTemplates(modal.templates);
              }
              clearSelection();
              setModalState(null);
              showToast('Daten erfolgreich importiert!');
            }}
            className="px-5 py-2 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 shadow-md shadow-red-500/20 transition-all active:scale-95"
          >
            Importieren & Überschreiben
          </button>
        </div>
      </Modal>
    );
  }

  if (modal.type === 'confirm-empty-trash') {
    return (
      <Modal title="Papierkorb leeren" onClose={() => setModalState(null)}>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
          Möchten Sie den Papierkorb wirklich unwiderruflich leeren?
        </p>
        <p className="text-xs text-red-500 font-medium mb-6">
          Alle gelöschten Prompts gehen dabei endgültig verloren. Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setModalState(null)}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={() => {
              actions.handleEmptyTrash();
              setModalState(null);
            }}
            className="px-5 py-2 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 shadow-md shadow-red-500/20 transition-all active:scale-95"
          >
            Papierkorb leeren
          </button>
        </div>
      </Modal>
    );
  }

  // ... rest of the modals (folder-color, export-format) follow same pattern
  // I'll skip them for brevity but they should be kept in a real implementation
  // Actually I should probably include them to avoid data loss.
  
  if (modal.type === 'folder-color') {
    const FOLDER_COLORS = [
      { label: 'Indigo',    hex: '#6366f1' },
      { label: 'Blau',      hex: '#3b82f6' },
      { label: 'Himmel',    hex: '#0ea5e9' },
      { label: 'Smaragd',   hex: '#10b981' },
      { label: 'Limette',   hex: '#84cc16' },
      { label: 'Gelb',      hex: '#eab308' },
      { label: 'Orange',    hex: '#f97316' },
      { label: 'Rot',       hex: '#ef4444' },
      { label: 'Rose',      hex: '#f43f5e' },
      { label: 'Pink',      hex: '#ec4899' },
      { label: 'Violett',   hex: '#8b5cf6' },
      { label: 'Grau',      hex: '#6b7280' },
    ];
    return (
      <Modal title="Ordnerfarbe wählen" onClose={() => setModalState(null)}>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Wählen Sie eine Farbe für diesen Ordner.
        </p>
        <div className="grid grid-cols-6 gap-2.5 mb-6">
          {FOLDER_COLORS.map(({ label, hex }) => (
            <button
              key={hex}
              onClick={() => {
                actions.handleSetFolderColor(modal.folderId, hex);
                setModalState(null);
              }}
              className={`w-10 h-10 rounded-xl transition-all duration-150 hover:scale-110 active:scale-95 ${
                modal.currentColor === hex
                  ? 'ring-2 ring-offset-2 ring-indigo-500'
                  : ''
              }`}
              style={{ backgroundColor: hex }}
              aria-label={label}
            />
          ))}
        </div>
        <div className="flex justify-between items-center pt-4 border-t border-gray-200/60 dark:border-gray-700/40">
          <button
            onClick={() => {
              actions.handleSetFolderColor(modal.folderId, undefined);
              setModalState(null);
            }}
            className="text-sm font-medium text-gray-500 hover:text-red-500 transition-colors"
          >
            Farbe entfernen
          </button>
          <button
            onClick={() => setModalState(null)}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
          >
            Abbrechen
          </button>
        </div>
      </Modal>
    );
  }

  if (modal.type === 'export-format') {
    return (
      <Modal title="Bibliothek exportieren" onClose={() => setModalState(null)}>
        <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
          Wählen Sie das gewünschte Format für den Export Ihrer Prompts und Ordner:
        </p>
        <div className="grid grid-cols-1 gap-3 mb-6">
          <button
            type="button"
            onClick={() => {
              actions.handleExportJson();
              setModalState(null);
            }}
            className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-indigo-500 dark:hover:border-indigo-500 bg-gray-50 dark:bg-gray-800 text-left transition-all hover:shadow-sm"
          >
            <div>
              <p className="font-semibold text-gray-900 dark:text-white text-sm">JSON (Vollständiges Backup)</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Enthält alle Ordner, Prompts, Skills, Workflows und Templates zur Wiederherstellung.</p>
            </div>
            <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 rounded">.json</span>
          </button>
          <button
            type="button"
            onClick={() => {
              actions.handleExportMarkdown();
              setModalState(null);
            }}
            className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-indigo-500 dark:hover:border-indigo-500 bg-gray-50 dark:bg-gray-800 text-left transition-all hover:shadow-sm"
          >
            <div>
              <p className="font-semibold text-gray-900 dark:text-white text-sm">Markdown</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Lesbare Dokumentation aller Prompts inklusive Tags und Metadaten.</p>
            </div>
            <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 rounded">.md</span>
          </button>
          <button
            type="button"
            onClick={() => {
              actions.handleExportCsv();
              setModalState(null);
            }}
            className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-indigo-500 dark:hover:border-indigo-500 bg-gray-50 dark:bg-gray-800 text-left transition-all hover:shadow-sm"
          >
            <div>
              <p className="font-semibold text-gray-900 dark:text-white text-sm">CSV (Tabellenkalkulation)</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Tabellarische Übersicht kompatibel mit Excel und Google Sheets.</p>
            </div>
            <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 rounded">.csv</span>
          </button>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setModalState(null)}
            className="btn btn-secondary btn-sm"
          >
            Schließen
          </button>
        </div>
      </Modal>
    );
  }

  return null;
};

export default AppModals;
